import React from "react";
import { BeatLoader } from "react-spinners";
import { motion } from "framer-motion";
import { useFetchPrice } from "../../../hooks/useFetchPrice";
import { useFetchLocations } from "../../../hooks/useFetchLocations";
import { usePageSettings } from "../../../contexts/PageSettingsContext";
import LocationListBox from "../inputs/LocationListbox";
import InputWithOptions from "../inputs/InputWithOptions";
import { useFetchPlans } from "../../../hooks/useFetchPlans";
import { useSelectedPlanContext } from "../../../contexts/SelectedPlanContext";
import PrimaryButton from "../inputs/buttons/Button";
import InputTableTitle from "./InputTableTitle";
import ErrorMessage from "../ErrorMessage";
import InputTableContactUs from "./InputTableContactUs";
import { constructUrl } from "../../../utils/constructUrl";
import { navigate } from "gatsby";
import { getTranslatedCityName } from "../../../utils/getTranslatedCityName";

const PrimaryInputTable = ({ serviceType, translations, defaultIataCode, pageContext }) => {
	const { selectedLocation, currency, pageSettingsContext } = usePageSettings();
	const { selectedPlan, changeSelectedPlan } = useSelectedPlanContext();
	const { city, language, country, hasCityInUrl, isDf, isHomepage } = pageSettingsContext;

	const queryLocations = useFetchLocations(serviceType, defaultIataCode);

	const queryPrice = useFetchPrice(serviceType);
	const queryPlans = useFetchPlans(
		serviceType,
		selectedLocation.iata_code !== "" ? selectedLocation.iata_code : defaultIataCode,
		currency,
		false
	);

	const motionContainer = {
		hidden: { opacity: 0 },
		visible: { opacity: 1 },
	};

	const motionItem = {
		hidden: { opacity: 0, y: 20 },
		visible: { opacity: 1, y: 0 },
	};

	const isPlansReady = queryPlans.data && queryPlans.data.length > 0;
	const isLoading = queryLocations.isFetching || queryPlans.isFetching || queryPrice.isFetching;

	// Auto select default plan when plans load
	React.useEffect(() => {
		if (isPlansReady && !selectedPlan?.id) {
			const defaultPlan = queryPlans.data.find((plan) => plan.default);
			if (defaultPlan) {
				const defaultResources = {
					cpu: defaultPlan.cpus?.find((cpu) => cpu.in_plan),
					ram: defaultPlan.rams?.find((ram) => ram.in_plan),
					storage: defaultPlan.storages?.find((s) => s.in_plan),
					bandwidth: defaultPlan.bandwidths?.find((b) => b.in_plan),
					transfer: defaultPlan.transfers?.find((t) => t.in_plan),
				};
				changeSelectedPlan({ ...defaultPlan, ...defaultResources, location_code: selectedLocation.iata_code, type: serviceType });
			}
		}
	}, [isPlansReady, queryPlans.data, selectedPlan?.id, selectedLocation.iata_code, serviceType, changeSelectedPlan]);

	// Auto select default plan when plans load or when plan is not valid for current service/location
	React.useEffect(() => {
		if (isPlansReady && selectedLocation?.iata_code) {
			const planExists = queryPlans.data.some((plan) => plan.id === selectedPlan?.id);
			const planMatchesService = selectedPlan?.type === serviceType;
			if (!selectedPlan?.id || !planExists || !planMatchesService || selectedPlan.location_code !== selectedLocation.iata_code) {
				const defaultPlan = queryPlans.data.find((plan) => plan.default) || queryPlans.data[0];
				if (defaultPlan) {
					const defaultResources = {
						cpu: defaultPlan.cpus?.find((cpu) => cpu.in_plan),
						ram: defaultPlan.rams?.find((ram) => ram.in_plan),
						storage: defaultPlan.storages?.find((s) => s.in_plan),
						bandwidth: defaultPlan.bandwidths?.find((b) => b.in_plan),
						transfer: defaultPlan.transfers?.find((t) => t.in_plan),
					};
					changeSelectedPlan({ ...defaultPlan, ...defaultResources, location_code: selectedLocation.iata_code });
				}
			}
		}
	}, [isPlansReady, selectedLocation?.iata_code, queryPlans.data, selectedPlan?.id, selectedPlan?.type, selectedPlan?.location_code, serviceType]);

	const onPlanChangeHandler = (planData) => {
		const defaultResources = {
			cpu: planData.cpus?.find((cpu) => cpu.in_plan),
			ram: planData.rams?.find((ram) => ram.in_plan),
			storage: planData.storages?.find((storage) => storage.in_plan),
			bandwidth: planData.bandwidths?.find((bandwidth) => bandwidth.in_plan),
			transfer: planData.transfers?.find((transfer) => transfer.in_plan),
		};
		changeSelectedPlan({
			...planData,
			...defaultResources,
			location_code: selectedLocation.iata_code,
			type: serviceType,
		});
	};

	const isPlanSelectionMatching = (plan, selected) => {
		if (!plan || !selected) return false;
		return plan.id === selected.id;
	};

	const onResourceChange = (resourceType, resourceValue) => {
		const updatedPlan = {
			...selectedPlan,
			[resourceType]: resourceValue,
		};
		changeSelectedPlan(updatedPlan);
	};

	let citySlug = hasCityInUrl ? getTranslatedCityName(language, city) : null;
	let countrySlug = isDf ? "df" : isHomepage ? null : country;

	const handleOrderNow = (event) => {
		event.preventDefault();
		// Construct the complete target URL using pageContext and constructUrl method
		const translatedCheckoutUrl = constructUrl(language, countrySlug, citySlug, "/checkout");
		sessionStorage.removeItem("checkout-tab");
		navigate(translatedCheckoutUrl);
	};

	const isLoadingPrice = queryLocations.isFetching || queryPlans.isFetching || queryPrice.isFetching;

	return (
		<section className="container  text-ipxon-black mx-auto flex flex-col gap-6  pb-20 w-full md:px-28 xl:px-0 px-4">
			<InputTableTitle
				serviceType={serviceType}
				selectedLocation={selectedLocation}
				translations={translations}
			/>

			{queryPlans.isError ? (
				<div className="flex justify-center bg-gray-50 min-h-[16.875rem] py-8 px-6 rounded-lg w-full">
					<ErrorMessage translations={translations} />
				</div>
			) : (
				<div className="flex flex-col xl:flex-row bg-gray-50 min-h-[16.875rem] py-8 px-6 gap-10 rounded-lg w-full justify-between">
					<div className={` flex flex-col gap-10`}>
						<div className="flex flex-col justify-center items-center uppercase gap-4">
							<h4 className="flex self-start uppercase font-bold text-xs">
								{translations["LabelForLocationListBox"]}
							</h4>
							<LocationListBox
								color="white"
								locationList={queryLocations.isFetching ? [] : queryLocations.data}
							/>
						</div>
						<div className="flex flex-col gap-4 h-full">
							<h4 className="uppercase text-xs font-bold">
								{translations["InputTableRecommendedPlans"]}
							</h4>
							<ul className={`list-none flex xl:flex-col gap-4 h-full justify-start`}>
								{queryPlans.isFetching ? (
									<span className="flex self-center justify-center w-full h-full items-center">
										<BeatLoader color="#d1d5db" size={8} />
									</span>
								) : (
									queryPlans.data?.map((planData) => (
										<li key={planData.id}>
											<label htmlFor={planData.id}>
												<div className="group flex gap-2 items-center">
													<input
														className={`form-radio appearance-none 2xl:h-4 2xl:w-4 checked:!bg-ipxon-magenta !text-ipxon-magenta focus:!bg-ipxon-magenta focus:ring-0 outline-none group-hover:cursor-pointer bg-none	`}
														type="radio"
														id={planData.id}
														name="plans"
														value={planData.id}
														checked={isPlanSelectionMatching(planData, selectedPlan)}
														onChange={() => onPlanChangeHandler(planData)
														}
													/>
													<p
														className={`group-hover:cursor-pointer font-bold uppercase text-xs ${
															isPlanSelectionMatching(planData, selectedPlan) && "text-ipxon-magenta"
														}`}
													>
														{planData.name}
													</p>
												</div>
											</label>
										</li>
									))
								)}
							</ul>
						</div>
					</div>

					<div className="xl:w-[55%] flex flex-col xl:flex-row gap-4 xl:gap-3 2xl:gap-4 justify-around">
						{!isPlansReady || !selectedPlan?.id ? (
							<span className="flex self-center justify-center w-full h-full items-center">
								<BeatLoader color="#d1d5db" size={16} />
							</span>
						) : (
							<>
								<InputWithOptions
									serviceType={serviceType}
									input={"CPU"}
									resources={selectedPlan?.cpus ?? []}
									translations={translations}
									vertical={true}
									onChangeResource={onResourceChange}
								/>
								<InputWithOptions
									serviceType={serviceType}
									input={"RAM"}
									resources={selectedPlan?.rams ?? []}
									translations={translations}
									vertical={true}
									onChangeResource={onResourceChange}
								/>
								<InputWithOptions
									serviceType={serviceType}
									input={"STORAGE"}
									resources={selectedPlan?.storages ?? []}
									translations={translations}
									vertical={true}
									onChangeResource={onResourceChange}
								/>
								<InputWithOptions
									serviceType={serviceType}
									input={"BANDWIDTH"}
									resources={selectedPlan?.bandwidths ?? []}
									translations={translations}
									vertical={true}
									onChangeResource={onResourceChange}
								/>
								<InputWithOptions
									serviceType={serviceType}
									input={"TRANSFER"}
									resources={selectedPlan?.transfers ?? []}
									translations={translations}
									vertical={true}
									onChangeResource={onResourceChange}
								/>
							</>
						)}
					</div>

					<div
						className={`xl:w-4/12 border-t xl:border-t-0 py-8 xl:py-0 xl:border-l border-gray-300 flex flex-col justify-center items-center font-bold uppercase gap-4 md:pl-6`}
					>
						<div className="flex flex-col justify-center items-center  text-3xl md:text-2xl 2xl:text-3xl gap-3">
							<motion.p
								initial="hidden"
								animate="visible"
								exit="hidden"
								variants={motionContainer}
								className="flex gap-1 h-7 items-center justify-center"
							>
								{queryPrice.isFetching ? (
									<BeatLoader color="#d1d5db" />
								) : (
									<motion.span variants={motionItem}>{queryPrice.data}</motion.span>
								)}
							</motion.p>
							<p className="text-gray-400 font-light text-center text-sm" key="payment">
								{translations["InputTableTimeSpan"]}
							</p>
						</div>
						<PrimaryButton
							onClick={handleOrderNow}
							className={`button-magenta flex reflect-below min-w-[175px] gap-1 h-8`}
						>
							{translations["InputTableButtonText"]}
						</PrimaryButton>
					</div>
				</div>
			)}

			<InputTableContactUs
				text={translations["InputTableDisclaimer"]}
				linkText={translations["InputTableDisclaimerLink"]}
				pageContext={pageContext}
			/>
		</section>
	);
};

// const capitalize = (s) => s && s[0].toUpperCase() + s.slice(1);

// const PrimaryInputTableContactUs = ({ text, linkText, pageContext }) => {
// 	const { selectedPlan } = useSelectedPlanContext();

// 	// Modify this part to check if service is 'vps'
// 	const serviceQueryParam = selectedPlan.service
// 		? selectedPlan.service === "vps"
// 			? "VPSMainTitle" // If 'vps', make it 'VPSMainTitle'
// 			: `${capitalize(selectedPlan.service)}MainTitle`
// 		: "";

// 	return (
// 		<div className="flex flex-wrap self-start text-start gap-2 text-sm">
// 			<p className="text-white">{text}</p>
// 			<Link
// 				to={`${useTranslatedUrl(
// 					pageContext.urlTranslations,
// 					pageContext.city,
// 					pageContext.language,
// 					"/contact-us"
// 				)}?service=${serviceQueryParam}`} // Append the service query parameter here
// 				className="text-ipxon-magenta font-bold hover:underline transition-all duration-500"
// 			>
// 				{linkText}
// 			</Link>
// 		</div>
// 	);
// };

export default PrimaryInputTable;
